import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from "react-bootstrap";
import Konsensus_Constants from '../constants/index.js';
import { Paper, Box, Breadcrumbs, Link, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import BottomTabNavigation from './BottomTabNavigation';
import CircularProgress from "@mui/material/CircularProgress";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaFolder, FaFile, FaDownload, FaTrash, FaUpload, FaArrowLeft, FaEye, FaSync } from 'react-icons/fa';

const Documents = () => {
  const scrollableDivRef = useRef(null);
  const navigate = useNavigate();
  const pollingIntervalRef = useRef(null);
  const moveCheckIntervalRef = useRef(null);
  const syncPollIntervalRef = useRef(null);
  const currentPathRef = useRef('');

  // State management
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState([{ name: 'Home', path: '' }]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState('stsma_generic');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [moving, setMoving] = useState(false);
  const [moveProgress, setMoveProgress] = useState('');
  const [moveComplete, setMoveComplete] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [selectedFileToMove, setSelectedFileToMove] = useState('');
  const [availableFolders, setAvailableFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');
  const [syncStats, setSyncStats] = useState({ imported: 0, totalFiles: 0, processing: 0 });

  // User permissions
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [isTrustee, setIsTrustee] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  const API_URL = Konsensus_Constants.API_URL || 'http://localhost:5001';

  // LocalStorage keys
  const UPLOAD_STATE_KEY = 'konsensus_upload_state';
  const NAVIGATION_STATE_KEY = 'konsensus_navigation_state';
  const MOVE_STATE_KEY = 'konsensus_move_state';
  const SYNC_STATE_KEY = 'konsensus_sync_state';

  // ========== UPLOAD STATE MANAGEMENT ==========

  // Save upload state to localStorage
  const saveUploadState = (state) => {
    localStorage.setItem(UPLOAD_STATE_KEY, JSON.stringify(state));
  };

  // Get upload state from localStorage
  const getUploadState = () => {
    const state = localStorage.getItem(UPLOAD_STATE_KEY);
    return state ? JSON.parse(state) : null;
  };

  // Clear upload state from localStorage
  const clearUploadState = () => {
    localStorage.removeItem(UPLOAD_STATE_KEY);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // ========== MOVE STATE MANAGEMENT ==========

  // Save move state to localStorage
  const saveMoveState = (state) => {
    localStorage.setItem(MOVE_STATE_KEY, JSON.stringify(state));
  };

  // Get move state from localStorage
  const getMoveState = () => {
    const state = localStorage.getItem(MOVE_STATE_KEY);
    return state ? JSON.parse(state) : null;
  };

  // Clear move state from localStorage
  const clearMoveState = () => {
    localStorage.removeItem(MOVE_STATE_KEY);
    if (moveCheckIntervalRef.current) {
      clearInterval(moveCheckIntervalRef.current);
      moveCheckIntervalRef.current = null;
    }
    setMoving(false);
    setMoveProgress('');
    setMoveComplete(false);
  };

  // ========== SYNC STATE MANAGEMENT ==========

  // Save sync state to localStorage
  const saveSyncState = (state) => {
    localStorage.setItem(SYNC_STATE_KEY, JSON.stringify(state));
  };

  // Get sync state from localStorage
  const getSyncState = () => {
    const state = localStorage.getItem(SYNC_STATE_KEY);
    return state ? JSON.parse(state) : null;
  };

  // Clear sync state from localStorage
  const clearSyncState = () => {
    localStorage.removeItem(SYNC_STATE_KEY);
    if (syncPollIntervalRef.current) {
      clearInterval(syncPollIntervalRef.current);
      syncPollIntervalRef.current = null;
    }
    setSyncing(false);
    setSyncProgress('');
    setSyncStats({ imported: 0, totalFiles: 0, processing: 0 });
  };

  // Check if move operation has completed
  const checkMoveCompletion = async (sourceFile, destinationFolder) => {
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes max (every 5 seconds)

    // Clear any existing interval
    if (moveCheckIntervalRef.current) {
      clearInterval(moveCheckIntervalRef.current);
      moveCheckIntervalRef.current = null;
    }

    moveCheckIntervalRef.current = setInterval(async () => {
      attempts++;
      console.log(`Checking move completion: attempt ${attempts}/${maxAttempts}`);

      try {
        const filename = sourceFile.split('/').pop();
        const fullDestination = destinationFolder
          ? `${destinationFolder}/${filename}`
          : filename;

        // Check if the file exists at the destination
        const response = await axios.get(`${API_URL}/api/gcs/files-folders`, {
          params: {
            bucketName: selectedBucket,
            prefix: destinationFolder || '',
            delimiter: '/'
          }
        });

        if (response.data.success) {
          const fileExists = response.data.files.some(file => file.name === fullDestination);

          if (fileExists) {
            console.log('✅ Move operation confirmed complete!');

            if (moveCheckIntervalRef.current) {
              clearInterval(moveCheckIntervalRef.current);
              moveCheckIntervalRef.current = null;
            }

            setMoveProgress('File moved successfully!');
            setMoveComplete(true);
            await new Promise(resolve => setTimeout(resolve, 2000));

            clearMoveState();

            // Use the current path from ref to fetch the correct folder
            const pathToFetch = currentPathRef.current;
            console.log('Fetching files for current path:', pathToFetch);
            fetchFiles(pathToFetch);
            return;
          }
        }
      } catch (error) {
        console.error('Error checking move completion:', error);
      }

      if (attempts >= maxAttempts) {
        console.warn('⏱️ Move verification timeout');

        if (moveCheckIntervalRef.current) {
          clearInterval(moveCheckIntervalRef.current);
          moveCheckIntervalRef.current = null;
        }

        clearMoveState();
        alert('Move verification timeout. The file may have moved. Please refresh to check.');
      }
    }, 5000);
  };

  // Check processing status from backend/Vertex API
  const checkProcessingStatus = async (fileName, bucketName) => {
    try {
      console.log(`🔍 Checking processing status for: ${fileName}`);

      const response = await axios.get(`${API_URL}/api/gcs/processing-status`, {
        params: {
          fileName: fileName,
          bucketName: bucketName
        }
      });

      if (response.data.success) {
        const { processing, complete, failed, exists } = response.data;

        console.log(`  Status - Processing: ${processing}, Complete: ${complete}, Failed: ${failed}, Exists: ${exists}`);

        return {
          isComplete: complete,
          isFailed: failed,
          isProcessing: processing,
          exists: exists
        };
      }

      return { isComplete: false, isFailed: false, isProcessing: false, exists: false };

    } catch (error) {
      console.error('Error checking processing status:', error);
      return { isComplete: false, isFailed: false, isProcessing: false, exists: false };
    }
  };

  // Poll for upload completion (Vertex AI processing)
  const pollForCompletion = (fileName, bucketName) => {
    let attempts = 0;
    const maxAttempts = 240; // 20 minutes max (every 5 seconds)
    let isComplete = false;

    pollingIntervalRef.current = setInterval(async () => {
      if (isComplete) {
        return;
      }

      attempts++;
      console.log(`📊 Polling attempt ${attempts}/${maxAttempts} for file: ${fileName}`);

      const status = await checkProcessingStatus(fileName, bucketName);

      if (status.isComplete && status.exists) {
        console.log('✅ Vertex AI processing complete!');
        isComplete = true;

        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        setProcessing(false);
        setUploading(false);
        setUploadProgress(0);

        // Get upload state BEFORE clearing it
        const uploadState = getUploadState();
        const uploadedToFolder = uploadState?.currentPath || currentPathRef.current || '';

        console.log('Upload complete. Path from uploadState:', uploadState?.currentPath, 'currentPathRef:', currentPathRef.current, 'Using:', uploadedToFolder);

        if (uploadedToFolder) {
          console.log('Navigating to uploaded folder:', uploadedToFolder);

          const buildBreadcrumbs = (path) => {
            const crumbs = [{ name: 'Home', path: '' }];

            if (!path) return crumbs;

            const parts = path.split('/').filter(Boolean);
            let currentCrumbPath = '';

            parts.forEach(part => {
              currentCrumbPath = currentCrumbPath ? `${currentCrumbPath}/${part}` : part;
              crumbs.push({ name: part, path: currentCrumbPath });
            });

            return crumbs;
          };

          const newBreadcrumbs = buildBreadcrumbs(uploadedToFolder);
          setCurrentPath(uploadedToFolder);
          setBreadcrumbs(newBreadcrumbs);
          saveNavigationState(uploadedToFolder, newBreadcrumbs);
        }

        // Clear upload state AFTER retrieving the path
        clearUploadState();

        // Fetch files with the explicit path to avoid stale state closure
        console.log('Fetching files for path after upload:', uploadedToFolder);
        fetchFiles(uploadedToFolder);

      } else if (status.isFailed) {
        console.error('❌ Vertex AI processing failed');
        isComplete = true;

        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        setProcessing(false);
        setUploading(false);
        setUploadProgress(0);
        clearUploadState();

        alert('File processing failed. Please try again.');

      } else if (attempts >= maxAttempts) {
        console.warn('⏱️ Vertex AI processing timeout');
        isComplete = true;

        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        setProcessing(false);
        setUploading(false);
        setUploadProgress(0);
        clearUploadState();

        alert('Processing verification timeout. The file may still be processing. Please refresh to check status.');
      }
    }, 5000);
  };

  // Check for ongoing uploads on component mount
  const checkOngoingUpload = () => {
    const uploadState = getUploadState();

    if (uploadState) {
      const { fileName, bucketName, timestamp } = uploadState;
      const timeSinceUpload = Date.now() - timestamp;

      console.log('Found ongoing upload state:', uploadState);
      console.log('Time since upload started:', timeSinceUpload / 1000, 'seconds');

      // If upload state is less than 20 minutes old, assume it might still be processing
      if (timeSinceUpload < 20 * 60 * 1000) {
        setUploading(true);
        setProcessing(true);
        setUploadProgress(100);

        // Start polling to check if Vertex AI processing is complete
        pollForCompletion(fileName, bucketName);
      } else {
        // Too old, clear it
        console.log('Upload state too old, clearing');
        clearUploadState();
      }
    }
  };

  // Check for ongoing move on component mount
  const checkOngoingMove = () => {
    const moveState = getMoveState();

    if (moveState) {
      const { fileName, destinationFolder, timestamp } = moveState;
      const timeSinceMove = Date.now() - timestamp;

      console.log('Found ongoing move state:', moveState);
      console.log('Time since move started:', timeSinceMove / 1000, 'seconds');

      // If move state is less than 5 minutes old, restore the UI state
      if (timeSinceMove < 5 * 60 * 1000) {
        console.log('Restoring move state');
        setMoving(true);
        setMoveProgress('Continuing file move...');
        setMoveComplete(false);
        setSelectedFileToMove(fileName);

        // Continue monitoring the move operation
        checkMoveCompletion(fileName, destinationFolder);
      } else {
        // Too old, clear it
        console.log('Move state too old, clearing');
        clearMoveState();
      }
    }
  };

  // Check for ongoing sync on component mount
    const checkOngoingSync = () => {
      const syncState = getSyncState();

      if (syncState) {
        const { bucketName, syncId, timestamp } = syncState;
        const timeSinceSync = Date.now() - timestamp;

        console.log('Found ongoing sync state:', syncState);
        console.log('Time since sync started:', timeSinceSync / 1000, 'seconds');

        // If sync state is less than 30 minutes old, restore the UI state
        if (timeSinceSync < 30 * 60 * 1000) {
          console.log('Restoring sync state with syncId:', syncId);
          setSyncing(true);
          setSyncProgress('Continuing sync operation...');

          // Poll for sync completion WITH syncId
          pollForSyncCompletion(bucketName, syncId);  // ← Pass syncId
        } else {
          // Too old, clear it
          console.log('Sync state too old, clearing');
          clearSyncState();
        }
      }
    };

  // Poll for sync completion with enhanced progress tracking
  const pollForSyncCompletion = (bucketName, syncId) => {
    let attempts = 0;
    const maxAttempts = 1440; // 120 minutes max (every 5 seconds) - Increased from 360 (30 min)

    // Clear any existing interval
    if (syncPollIntervalRef.current) {
      clearInterval(syncPollIntervalRef.current);
      syncPollIntervalRef.current = null;
    }

    console.log('🔄 Starting sync polling with syncId:', syncId);
    console.log(`   Max duration: ${maxAttempts * 5 / 60} minutes`);

    syncPollIntervalRef.current = setInterval(async () => {
      attempts++;
      console.log(`🔄 Polling sync status: attempt ${attempts}/${maxAttempts}`);

      try {
        // Build URL with syncId parameter
        const params = new URLSearchParams({
          bucketName: bucketName,
          prefix: ''
        });

        // Add syncId if available
        if (syncId) {
          params.append('syncId', syncId);
        }

        const response = await axios.get(`${API_URL}/api/gcs/sync-status?${params.toString()}`);

        if (response.data.success) {
          const { status } = response.data;

          console.log('📊 Sync status:', status);

          // Update sync stats
          setSyncStats({
            imported: status.imported || 0,
            totalFiles: status.totalFiles || 0,
            processing: status.processing || 0,
            failed: status.failed || 0,
            skipped: status.skipped || 0
          });

          // Update progress message with detailed info
          if (status.processing > 0 || status.notImported > 0) {
            setSyncProgress(
              `Syncing... ${status.imported}/${status.totalFiles} documents processed ` +
              `(${status.processing} in progress, ${status.failed || 0} failed)`
            );
          }

          // ========== IMPROVED COMPLETION CHECK ==========
          // Check if sync is complete using multiple conditions:
          // 1. syncStatus === 'complete' (backend marks it complete)
          // 2. All files processed: imported + failed === totalFiles
          // 3. No files processing and no files waiting
          const allFilesProcessed = status.totalFiles > 0 &&
                                     (status.imported + (status.failed || 0)) === status.totalFiles;

          const noFilesRemaining = (status.processing === 0 && status.notImported === 0);

          const isComplete = status.syncStatus === 'complete' ||
                            allFilesProcessed ||
                            noFilesRemaining;

          if (isComplete) {
            console.log('✅ Sync complete!');
            console.log(`   Total files: ${status.totalFiles}`);
            console.log(`   Imported: ${status.imported}`);
            console.log(`   Failed: ${status.failed || 0}`);
            console.log(`   Skipped: ${status.skipped || 0}`);
            console.log(`   Completion reason: ${
              status.syncStatus === 'complete' ? 'Backend marked complete' :
              allFilesProcessed ? 'All files processed' :
              'No files remaining'
            }`);

            if (syncPollIntervalRef.current) {
              clearInterval(syncPollIntervalRef.current);
              syncPollIntervalRef.current = null;
            }

            setSyncProgress(
              `✅ Sync complete! Total: ${status.totalFiles} documents | ` +
              `Imported: ${status.imported} | Skipped: ${status.skipped || 0} | Failed: ${status.failed || 0}`
            );

            setTimeout(() => {
              clearSyncState();
              fetchFiles();
            }, 5000);
          }
        }
      } catch (error) {
        console.error('Error checking sync status:', error);
      }

      if (attempts >= maxAttempts) {
        console.warn('⏱️ Sync status check timeout');
        console.warn(`   Reached ${maxAttempts} attempts (${maxAttempts * 5 / 60} minutes)`);

        if (syncPollIntervalRef.current) {
          clearInterval(syncPollIntervalRef.current);
          syncPollIntervalRef.current = null;
        }

        setSyncProgress('Sync check timeout. Please refresh to verify.');

        setTimeout(() => {
          clearSyncState();
        }, 5000);
      }
    }, 5000);
  };

  // ========== NAVIGATION STATE MANAGEMENT ==========

  // Save navigation state to localStorage
  const saveNavigationState = (path, crumbs) => {
    localStorage.setItem(NAVIGATION_STATE_KEY, JSON.stringify({
      currentPath: path,
      breadcrumbs: crumbs
    }));
  };

  // Get navigation state from localStorage
  const getNavigationState = () => {
    const state = localStorage.getItem(NAVIGATION_STATE_KEY);
    return state ? JSON.parse(state) : null;
  };

  // Restore navigation state on component mount
  const restoreNavigationState = () => {
    const navState = getNavigationState();
    if (navState) {
      console.log('Restoring navigation state:', navState);
      setCurrentPath(navState.currentPath);
      setBreadcrumbs(navState.breadcrumbs);
    }
  };

  // ========== LOGIN AND PERMISSIONS ==========

  // Check login and permissions
  const checkLogin = async() => {
    if(window.location.href != 'https://app.konsensus.tech/' &&
       window.location.href != 'https://app.dev.konsensus.tech/' &&
       window.location.href != 'https://app.accept.konsensus.tech/') {
      try {
        let login_data = await Konsensus_Constants._retrieveDataLocal("login_response");
        let nem = await Konsensus_Constants._retrieveDataLocal("nem");

        setUserId(login_data.login_response.data.userId);
        setEmail(nem.nem.data.email);
        setUsername(nem.nem.data.username);

        if(login_data.login_response.data) {
          let res = await fetch(
            Konsensus_Constants.FORUM_URL + '/api/users/' + login_data.login_response.data.userId + "?include=groups",
            { headers: { 'Authorization': 'Token ' + login_data.login_response.data.token }}
          );

          if (res.status === 200) {
            const userData = await res.json();
            const allIncludedGroups = userData.included?.filter(item => item.type === 'groups') || [];

            const trusteeStatus = allIncludedGroups.some(group =>
              group.attributes?.nameSingular === 'Trustee' ||
              group.attributes?.namePlural === 'Trustees'
            );

            const adminStatus = allIncludedGroups.some(group =>
              group.attributes?.nameSingular === 'Admin' ||
              group.attributes?.namePlural === 'Admins'
            );

            setIsTrustee(trusteeStatus);
            setIsAdmin(adminStatus);
            setCanEdit(trusteeStatus || adminStatus);

            console.log("User permissions - Trustee:", trusteeStatus, "Admin:", adminStatus);
          } else {
            // Clear all localStorage on logout
            Konsensus_Constants.clear();
            localStorage.removeItem(NAVIGATION_STATE_KEY);
            localStorage.removeItem(UPLOAD_STATE_KEY);
            localStorage.removeItem(MOVE_STATE_KEY);
            localStorage.removeItem(SYNC_STATE_KEY);
            clearUploadState();
            clearMoveState();
            clearSyncState();
            navigate('/', { state: { message: "Login expired or email not confirmed!" }});
          }
        }
      } catch (error) {
        console.error(error);
        // Clear all localStorage on error
        Konsensus_Constants.clear();
        localStorage.removeItem(NAVIGATION_STATE_KEY);
        localStorage.removeItem(UPLOAD_STATE_KEY);
        localStorage.removeItem(MOVE_STATE_KEY);
        localStorage.removeItem(SYNC_STATE_KEY);
        clearUploadState();
        clearMoveState();
        clearSyncState();
        navigate('/', { state: { message: "Login expired or email not confirmed!" }});
      }
    }
  };

  // ========== COMPONENT LIFECYCLE ==========

  useEffect(() => {
    const init = async () => {
      restoreNavigationState(); // Restore navigation first
      await checkLogin();
      checkOngoingUpload(); // Check for ongoing uploads
      checkOngoingMove(); // Check for ongoing moves
      checkOngoingSync(); // Check for ongoing syncs
      setInitialized(true); // Mark as initialized
    };

    init();

    // Cleanup polling on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (moveCheckIntervalRef.current) {
        clearInterval(moveCheckIntervalRef.current);
      }
      if (syncPollIntervalRef.current) {
        clearInterval(syncPollIntervalRef.current);
      }
    };
  }, []);

  // Fetch files whenever initialized changes or currentPath changes
  useEffect(() => {
    if (initialized) {
      console.log('Component initialized, fetching files for path:', currentPath);
      fetchFiles(currentPath);
    }
  }, [initialized]);

  // Update ref when currentPath changes
  useEffect(() => {
    currentPathRef.current = currentPath;
  }, [currentPath]);

  // ========== FILE OPERATIONS ==========

  // Fetch files
  const fetchFiles = async (path = currentPath) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/gcs/files-folders`, {
        params: {
          bucketName: selectedBucket,
          prefix: path || '',
          delimiter: '/'
        }
      });

      if (response.data.success) {
        setFolders(response.data.folders || []);
        setFiles(response.data.files || []);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);
    }
  };

  // Delete folder
  const handleDeleteFolder = async (folderName) => {
    if (!canEdit) {
      alert("You don't have permission to delete folders");
      return;
    }

    if (!window.confirm(`Delete folder "${folderName}" and all its contents?`)) return;

    const folderPath = currentPath ? `${currentPath}/${folderName}` : folderName;

    try {
      const response = await axios.delete(`${API_URL}/api/gcs/folder/delete`, {
        params: {
          folderPath,
          bucketName: selectedBucket,
        },
      });

      if (response.data.success) {
        fetchFiles();
      }
    } catch (error) {
      console.error('Delete folder error:', error);
      alert('Failed to delete folder');
    }
  };

  // Navigate to folder
  const handleFolderClick = (folderName) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    const newBreadcrumbs = [...breadcrumbs, { name: folderName, path: newPath }];

    setCurrentPath(newPath);
    setBreadcrumbs(newBreadcrumbs);
    saveNavigationState(newPath, newBreadcrumbs);
    fetchFiles(newPath);
  };

  // Navigate using breadcrumb
  const navigateToBreadcrumb = (index) => {
    const newPath = breadcrumbs[index].path;
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);

    setCurrentPath(newPath);
    setBreadcrumbs(newBreadcrumbs);
    saveNavigationState(newPath, newBreadcrumbs);
    fetchFiles(newPath);
  };

  // Upload file
  const handleFileUpload = async (e) => {
    if (!canEdit) {
      alert("You don't have permission to upload files");
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    let nem = await Konsensus_Constants._retrieveDataLocal("nem");

    if( nem && nem.nem && nem.nem.data ){
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('bucketName', selectedBucket);
        formData.append('folder', currentPath);
        formData.append('makePublic', 'false');
        formData.append('userhash', nem.nem.data.userhash);

        const fileName = currentPath ? `${currentPath}/${file.name}` : file.name;

        try {
          const response = await axios.post(
            `${API_URL}/api/gcs/upload`,
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
              onUploadProgress: (progressEvent) => {
                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(progress);

                if (progress >= 100) {
                  setProcessing(true);
                  // Save upload state to localStorage
                  saveUploadState({
                    fileName: fileName,
                    bucketName: selectedBucket,
                    timestamp: Date.now()
                  });
                }
              }
            }
          );

          if (response.data.success) {
            // Upload complete, now start monitoring Vertex AI processing
            console.log('Upload complete, starting Vertex AI processing monitoring...');
            pollForCompletion(fileName, selectedBucket);
          }
        } catch (error) {
          console.error('Upload error:', error);
          setProcessing(false);
          setUploading(false);
          setUploadProgress(0);
          clearUploadState();
          alert('Failed to upload file');
        }
    }
  };

  // Download file
  const handleDownload = async (filename) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/gcs/download/${encodeURIComponent(filename)}`,
        {
          params: { bucketName: selectedBucket },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename.split('/').pop());
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file');
    }
  };

  // View file
  const handleView = async (file) => {
    try {
      // If we already have a signed URL, use it
      if (file.signedUrl) {
        window.open(file.signedUrl, '_blank');
        return;
      }

      // Otherwise, request a signed URL for viewing
      const response = await axios.get(`${API_URL}/api/gcs/signed-url`, {
        params: {
          fileName: file.name,
          bucketName: selectedBucket,
          action: 'read'
        }
      });

      if (response.data.success && response.data.signedUrl) {
        window.open(response.data.signedUrl, '_blank');
      } else {
        // Fallback to download if signed URL fails
        handleDownload(file.name);
      }
    } catch (error) {
      console.error('View error:', error);
      // Fallback to download on error
      handleDownload(file.name);
    }
  };

  // Delete file
  const handleDelete = async (filename) => {
    if (!canEdit) {
      alert("You don't have permission to delete files");
      return;
    }

    if (!window.confirm(`Delete ${filename}?`)) return;

    try {
      const response = await axios.delete(
        `${API_URL}/api/gcs/delete/${encodeURIComponent(filename)}`,
        { params: { bucketName: selectedBucket } }
      );

      if (response.data.success) {
        fetchFiles();
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete file');
    }
  };

  // Create folder
  const handleCreateFolder = async () => {
    if (!canEdit) {
      alert("You don't have permission to create folders");
      return;
    }

    const folderName = prompt("Enter folder name:");
    if (!folderName) return;

    const folderPath = currentPath ? `${currentPath}/${folderName}` : folderName;

    try {
      const response = await axios.post(`${API_URL}/api/gcs/folder/create`, {
        folderPath,
        bucketName: selectedBucket
      });

      if (response.data.success) {
        fetchFiles();
      }
    } catch (error) {
      console.error('Create folder error:', error);
      alert('Failed to create folder');
    }
  };

// Fixed fetchAllFolders - handles string comparison properly
  const fetchAllFolders = async () => {
    setLoadingFolders(true);

    // Extract current folder - TRIM and normalize
    const currentFileFolder = selectedFileToMove.includes('/')
      ? selectedFileToMove.substring(0, selectedFileToMove.lastIndexOf('/')).trim()
      : '';

    console.log('='.repeat(70));
    console.log('🔍 FETCHING FOLDERS FOR MOVE');
    console.log('='.repeat(70));
    console.log('📄 File to move:', `"${selectedFileToMove}"`);
    console.log('📁 Current folder (extracted):', `"${currentFileFolder}"`);
    console.log('📁 Current folder length:', currentFileFolder.length);
    console.log('📁 Current folder char codes:', Array.from(currentFileFolder).map(c => c.charCodeAt(0)));

    try {
      const response = await axios.get(`${API_URL}/api/gcs/search`, {
        params: {
          bucketName: selectedBucket,
          query: ''
        }
      });

      console.log('✅ API Response received');
      console.log('   Files count:', response.data.files?.length || 0);

      // ALWAYS start with Home folder
      const folderSet = new Set();
      folderSet.add(''); // Explicitly add empty string for Home

      console.log('✅ Added Home folder to set (empty string)');
      console.log('   Set size after adding Home:', folderSet.size);
      console.log('   Set contents:', Array.from(folderSet));

      if (response.data.success && response.data.files && response.data.files.length > 0) {
        console.log('📂 Extracting folders from files...');

        response.data.files.forEach((file, idx) => {
          if (idx < 3) {
            console.log(`   Sample file ${idx + 1}: "${file.name}"`);
          }

          const pathParts = file.name.split('/');

          for (let i = 0; i < pathParts.length - 1; i++) {
            const folderPath = pathParts.slice(0, i + 1).join('/').trim();
            if (folderPath) {
              folderSet.add(folderPath);
            }
          }
        });
      }

      const allFolders = Array.from(folderSet);
      console.log('');
      console.log('📋 ALL FOLDERS EXTRACTED (before filtering):');
      allFolders.forEach((folder, idx) => {
        console.log(`   ${idx + 1}. "${folder || '(HOME)'}" [length: ${folder.length}]`);
      });
      console.log('   Total:', allFolders.length);

      // CRITICAL: Detailed filtering with string comparison
      console.log('');
      console.log('🔍 FILTERING LOGIC - String Comparison:');
      console.log(`   Target to exclude: "${currentFileFolder}" [length: ${currentFileFolder.length}]`);
      console.log('');

      const folderList = allFolders.filter(folder => {
        // Normalize both strings for comparison
        const normalizedFolder = folder.trim();
        const normalizedCurrent = currentFileFolder.trim();

        // Check if they match
        const exactMatch = normalizedFolder === normalizedCurrent;

        // Additional checks
        const lengthMatch = normalizedFolder.length === normalizedCurrent.length;
        const caseInsensitiveMatch = normalizedFolder.toLowerCase() === normalizedCurrent.toLowerCase();

        console.log(`   Comparing: "${folder || '(HOME)'}"`);
        console.log(`      Folder length: ${folder.length}, Current length: ${currentFileFolder.length}`);
        console.log(`      Exact match: ${exactMatch}`);
        console.log(`      Length match: ${lengthMatch}`);
        console.log(`      Case-insensitive match: ${caseInsensitiveMatch}`);

        // Should we KEEP this folder? (Keep if NOT the current folder)
        const shouldKeep = !exactMatch;

        console.log(`      Decision: ${shouldKeep ? '✅ KEEP' : '❌ EXCLUDE'}`);
        console.log('');

        return shouldKeep;
      });

      // Sort with Home first
      folderList.sort((a, b) => {
        if (a === '') return -1;
        if (b === '') return 1;
        return a.localeCompare(b);
      });

      console.log('');
      console.log('✅ FINAL FILTERED FOLDERS:');
      folderList.forEach((folder, idx) => {
        console.log(`   ${idx + 1}. "${folder || '(HOME)'}"`);
      });
      console.log('   Total:', folderList.length);
      console.log('   Home included?', folderList.includes('') ? '✅ YES' : '❌ NO');
      console.log('='.repeat(70));

      // Double-check: If Home is missing and file is NOT in Home, force add it
      if (!folderList.includes('') && currentFileFolder !== '') {
        console.log('⚠️ WARNING: Home folder was filtered out incorrectly!');
        console.log('🔧 FORCING Home folder to be added...');
        folderList.unshift(''); // Add Home at beginning
        console.log('✅ Home folder force-added');
      }

      setAvailableFolders(folderList);

    } catch (error) {
      console.error('❌ ERROR:', error);

      // Fallback
      if (currentFileFolder !== '') {
        console.log('⚠️ Using fallback: Offering Home folder');
        setAvailableFolders(['']);
      } else {
        setAvailableFolders([]);
      }

    } finally {
      setLoadingFolders(false);
    }
  };

  // Open move dialog
  const handleMoveFile = async (fileName) => {
    if (!canEdit) {
      alert("You don't have permission to move files");
      return;
    }

    setSelectedFileToMove(fileName);
    setMoveDialogOpen(true);
    await fetchAllFolders();
  };

  // Execute file move
  const executeMove = async (destinationFolder) => {
    setMoveDialogOpen(false);

    // ✅ SAFEGUARD: Check if file is already in the selected folder
    const currentFileFolder = selectedFileToMove.includes('/')
      ? selectedFileToMove.substring(0, selectedFileToMove.lastIndexOf('/'))
      : '';

    console.log('Move operation:');
    console.log('  File:', selectedFileToMove);
    console.log('  From folder:', currentFileFolder || 'Home');
    console.log('  To folder:', destinationFolder || 'Home');

    if (currentFileFolder === destinationFolder) {
      alert('File is already in this folder!');
      setSelectedFileToMove('');
      return;
    }

    // Clear any existing move check interval
    if (moveCheckIntervalRef.current) {
      clearInterval(moveCheckIntervalRef.current);
      moveCheckIntervalRef.current = null;
    }

    setMoving(true);
    setMoveProgress(`Moving to ${destinationFolder || 'Home'}...`);
    setMoveComplete(false);

    // Save move state to localStorage
    saveMoveState({
      fileName: selectedFileToMove,
      destinationFolder: destinationFolder,
      timestamp: Date.now()
    });

    try {
      const filename = selectedFileToMove.split('/').pop();

      // Build destination path
      // If destinationFolder is empty string, file goes to root
      const fullDestination = destinationFolder
        ? `${destinationFolder}/${filename}`
        : filename; // Just filename = root folder

      console.log('  Full source path:', selectedFileToMove);
      console.log('  Full destination path:', fullDestination);

      // ✅ SAFEGUARD: Double-check source !== destination
      if (selectedFileToMove === fullDestination) {
        console.error('❌ Source and destination are the same!');
        clearMoveState();
        alert('Cannot move file to the same location!');
        return;
      }

      setMoveProgress('Moving file...');

      const response = await axios.post(`${API_URL}/api/gcs/move`, {
        bucketName: selectedBucket,
        source: selectedFileToMove,
        destination: fullDestination,
      });

      if (response.data.success) {
        if (response.data.warning) {
          // File copied but source not deleted
          setMoveProgress(response.data.warning);
          alert(response.data.message);
        } else if (response.data.vertexUpdated) {
          setMoveProgress('File moved! Updating Knowledge Base...');
          await new Promise(resolve => setTimeout(resolve, 1500));
          setMoveProgress('Updated successfully!');
        } else {
          setMoveProgress('File moved successfully!');
        }

        setMoveComplete(true);
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Verify the move was successful
        setMoveProgress('Verifying move...');

        const sourceFolder = selectedFileToMove.includes('/')
          ? selectedFileToMove.substring(0, selectedFileToMove.lastIndexOf('/'))
          : '';

        const verifyResponse = await axios.get(`${API_URL}/api/gcs/files-folders`, {
          params: {
            bucketName: selectedBucket,
            prefix: sourceFolder,
            delimiter: '/'
          }
        });

        if (verifyResponse.data.success) {
          const fileStillExists = verifyResponse.data.files.some(
            file => file.name === selectedFileToMove
          );

          if (!fileStillExists) {
            console.log('✅ Move verified - source file no longer exists');
            setMoveProgress('Move verified successfully!');
            await new Promise(resolve => setTimeout(resolve, 1000));

            clearMoveState();
            fetchFiles();
          } else {
            console.log('⏳ Source file still exists, starting monitoring...');
            checkMoveCompletion(selectedFileToMove, destinationFolder);
          }
        }
      }
    } catch (error) {
      console.error('Move error:', error);
      clearMoveState();
      alert('Failed to move file: ' + (error.response?.data?.error || error.message));
    }
  };

  // Search files
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchFiles();
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/gcs/search`, {
        params: {
          bucketName: selectedBucket,
          query: searchQuery
        }
      });

      if (response.data.success) {
        const folderSet = new Set();
        const fileList = [];

        response.data.files.forEach(file => {
          const relativePath = currentPath
            ? file.name.substring(currentPath.length).replace(/^\//, '')
            : file.name;

          const pathParts = relativePath.split('/');

          if (pathParts.length > 1 && pathParts[0]) {
            folderSet.add(pathParts[0]);
          } else if (pathParts[0] && !file.name.endsWith('/')) {
            fileList.push(file);
          }
        });

        setFolders(Array.from(folderSet).sort());
        setFiles(fileList.sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sync all files to Vertex AI - NOW WITH forceResync PARAMETER
  const handleSyncToVertex = async (forceResync = false) => {
    if (!canEdit) {
      alert("You don't have permission to sync files");
      return;
    }

    const confirmMessage = forceResync
      ? 'Force sync all files in this bucket? This will re-import all files, even if they were already synced. This may take several minutes.'
      : 'Sync all files in this bucket? This may take several minutes.';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setSyncing(true);
    setSyncProgress(forceResync ? 'Starting force sync...' : 'Starting sync...');
    setSyncStats({ imported: 0, totalFiles: 0, processing: 0 });
    let cleanupOrphans;
    if( forceResync ){
        cleanupOrphans = true;
    }else{
        cleanupOrphans = false;
    }

    try {
      const response = await axios.post(`${API_URL}/api/gcs/sync-to-vertex`, {
        bucketName: selectedBucket,
        prefix: '',
        forceResync: forceResync,
        cleanupOrphans: cleanupOrphans
      });

      if (response.data.success) {
        const { summary, syncId } = response.data;

        console.log('🚀 Sync started with ID:', syncId);
        console.log('   Summary:', summary);

        // Update initial stats
        setSyncStats({
          imported: summary.imported || 0,
          totalFiles: summary.totalFiles || 0,
          processing: summary.processing || 0,
          failed: summary.failed || 0,
          skipped: summary.skipped || 0
        });

        // Save sync state to localStorage WITH syncId
        saveSyncState({
          bucketName: selectedBucket,
          syncId: syncId,  // ← IMPORTANT: Save the syncId
          forceResync: forceResync,
          timestamp: Date.now()
        });

        // If sync completed immediately (all files were already synced)
        if (summary.processing === 0 && summary.notImported === 0) {
          setSyncProgress(
            `✅ Sync complete! Total: ${summary.totalFiles} documents | ` +
            `Imported: ${summary.imported} | Skipped: ${summary.skipped || 0} | Failed: ${summary.failed || 0}`
          );

          setTimeout(() => {
            clearSyncState();
            fetchFiles();
          }, 5000);
        } else {
          // Start polling for completion WITH syncId
          setSyncProgress(
            `Syncing... ${summary.imported}/${summary.totalFiles} documents processed ` +
            `(${summary.processing} in progress)`
          );
          pollForSyncCompletion(selectedBucket, syncId);  // ← Pass syncId
        }
      }
    } catch (error) {
      console.error('Sync error:', error);
      setSyncProgress('Sync failed: ' + (error.response?.data?.error || error.message));

      setTimeout(() => {
        clearSyncState();
      }, 5000);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Box sx={{
      height: "100vh",
      width: "100vw",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      position: "fixed",
      top: 0,
      left: 0
    }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 2 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={require('../assets/images/banner.png')}
              style={{
                height: 97,
                width: 380,
                marginBottom: 0,
                marginTop: 20,
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              style={{
                padding: '6px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                width: '200px'
              }}
            />

            <Button
              onClick={handleSearch}
              style={{
                backgroundColor: "#00aaaf",
                borderColor: "#00aaaf",
                fontSize: 12,
                padding: '6px 12px'
              }}
            >
              Search
            </Button>

            <Button
              onClick={() => fetchFiles()}
              style={{
                backgroundColor: "#00aaaf",
                borderColor: "#00aaaf",
                fontSize: 12,
                padding: '6px 12px'
              }}
            >
              Refresh
            </Button>

            {/* REGULAR SYNC BUTTON */}
            {canEdit && (
              <Button
                onClick={() => handleSyncToVertex(false)}
                disabled={syncing}
                style={{
                  backgroundColor: syncing ? '#ccc' : '#34a853',
                  borderColor: syncing ? '#ccc' : '#34a853',
                  fontSize: 12,
                  padding: '6px 12px',
                  opacity: syncing ? 0.6 : 1,
                  cursor: syncing ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Sync new/updated files only"
              >
                {syncing ? (
                  <>
                    <CircularProgress size={12} sx={{ color: 'white' }} />
                    <span>Syncing...</span>
                  </>
                ) : (
                  <>
                    <FaSync size={12} />
                    <span>Sync</span>
                  </>
                )}
              </Button>
            )}

            {/* FORCE SYNC BUTTON */}
            {isAdmin && (
              <Button
                onClick={() => handleSyncToVertex(true)}
                disabled={syncing}
                style={{
                  backgroundColor: syncing ? '#ccc' : '#ea4335',
                  borderColor: syncing ? '#ccc' : '#ea4335',
                  fontSize: 12,
                  padding: '6px 12px',
                  opacity: syncing ? 0.6 : 1,
                  cursor: syncing ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Force sync all files (re-import everything)"
              >
                <FaSync size={12} />
                <span>Force Sync</span>
              </Button>
            )}

            {canEdit && (
              <>
                <Button
                  onClick={handleCreateFolder}
                  style={{
                    backgroundColor: "#00aaaf",
                    borderColor: "#00aaaf",
                    fontSize: 12,
                    padding: '6px 12px'
                  }}
                >
                  + Folder
                </Button>

                <label style={{
                  padding: '6px 12px',
                  backgroundColor: uploading ? '#ccc' : '#fbbc04',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  opacity: uploading ? 0.6 : 1
                }}>
                  <FaUpload style={{ marginRight: 4 }} />
                  Upload
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    disabled={uploading}
                  />
                </label>
              </>
            )}
          </div>
        </div>
      </Paper>

      {/* Main Content */}
      <Paper elevation={3} sx={{
        flex: 1,
        width: "100%",
        p: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
      }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs>
            {breadcrumbs.map((crumb, index) => (
              <Link
                key={index}
                component="button"
                variant="body1"
                onClick={() => navigateToBreadcrumb(index)}
                sx={{
                  color: '#00aaaf',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {crumb.name}
              </Link>
            ))}
          </Breadcrumbs>
        </Box>

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Box sx={{
              width: '100%',
              height: '24px',
              bgcolor: '#e0e0e0',
              borderRadius: '12px',
              overflow: 'hidden',
              mb: 1
            }}>
              <Box sx={{
                width: `${uploadProgress}%`,
                height: '100%',
                bgcolor: processing ? '#fbbc04' : '#00aaaf',
                transition: 'width 0.3s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {uploadProgress > 10 && (
                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                    {uploadProgress}%
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {processing && <CircularProgress size={16} sx={{ color: '#fbbc04' }} />}
              <Typography variant="body2" sx={{ fontWeight: 500, color: processing ? '#fbbc04' : '#00aaaf' }}>
                {processing ? 'Updating the Knowledge Base...' : `Uploading: ${uploadProgress}%`}
              </Typography>
            </Box>
            {processing && (
              <Typography variant="caption" sx={{ color: '#666', mt: 0.5, display: 'block' }}>
                You can carry on working so long...
              </Typography>
            )}
          </Box>
        )}

        {/* Move Progress */}
        {moving && (
          <Box sx={{
            mb: 2,
            p: 2,
            bgcolor: moveComplete ? '#e8f5e9' : '#fff3e0',
            borderRadius: 2,
            border: `2px solid ${moveComplete ? '#4caf50' : '#ff9800'}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {!moveComplete && <CircularProgress size={20} sx={{ color: '#ff9800' }} />}
              <Typography variant="body2" sx={{
                fontWeight: 600,
                color: moveComplete ? '#2e7d32' : '#e65100'
              }}>
                {moveComplete ? '✅' : '📦'} {moveProgress}
              </Typography>
            </Box>
            {!moveComplete && (
              <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                Please wait while the file is being moved...
              </Typography>
            )}
          </Box>
        )}

        {/* ENHANCED SYNC PROGRESS - Shows document count */}
        {syncing && (
          <Box sx={{
            mb: 2,
            p: 2,
            bgcolor: '#e3f2fd',
            borderRadius: 2,
            border: '2px solid #2196f3'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CircularProgress size={20} sx={{ color: '#1976d2' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0' }}>
                {syncProgress}
              </Typography>
            </Box>

            {/* Document Statistics */}
            {syncStats.totalFiles > 0 && (
              <Box sx={{ mt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1565c0' }}>
                    Total:
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    {syncStats.totalFiles} documents
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    Imported:
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    {syncStats.imported}
                  </Typography>
                </Box>
                {syncStats.processing > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#f57c00' }}>
                      In Progress:
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      {syncStats.processing}
                    </Typography>
                  </Box>
                )}
                {syncStats.failed > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#c62828' }}>
                      Failed:
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      {syncStats.failed}
                    </Typography>
                  </Box>
                )}
                {syncStats.skipped > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#616161' }}>
                      Skipped:
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      {syncStats.skipped}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
              Please wait while documents are being synced to the Knowledge Base...
            </Typography>
          </Box>
        )}

        {/* File List */}
        <div
          ref={scrollableDivRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          {loading ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 4
            }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
                Loading files...
              </Typography>
            </Box>
          ) : (
            <>
              {/* Folders */}
              {folders.map((folder, index) => (
                <Box
                  key={`folder-${index}`}
                  onClick={() => handleFolderClick(folder)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 2,
                    marginBottom: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: '#f5f5f5'
                    }
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', flex: 1 }}
                    onClick={() => handleFolderClick(folder)}
                  >
                    <FaFolder size={20} color="#00aaaf" style={{ marginRight: 12 }} />
                    <Typography sx={{ color: '#00aaaf', fontWeight: 500 }}>
                      {folder}
                    </Typography>
                  </div>

                  {canEdit && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder);
                      }}
                      sx={{ color: '#ea4335' }}
                      title="Delete Folder"
                    >
                      <FaTrash size={16} />
                    </IconButton>
                  )}

                </Box>
              ))}

              {/* Files */}
              {files.map((file, index) => {
                const fileName = file.name.split('/').pop();
                return (
                  <Box
                    key={`file-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 2,
                      marginBottom: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: '#f5f5f5'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <FaFile size={20} color="#666" style={{ marginRight: 12 }} />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {fileName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {formatFileSize(file.size)} • {formatDate(file.updated)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleView(file)}
                          sx={{ color: '#00aaaf' }}
                          title="View"
                        >
                          <FaEye size={16} />
                        </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(file.name)}
                        title="Download"
                      >
                        <FaDownload size={16} />
                      </IconButton>

                      {canEdit && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveFile(file.name)}
                            title="Move file"
                            sx={{ color: '#00aaaf' }}
                          >
                            <FaArrowLeft size={16} />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={() => handleDelete(file.name)}
                            title="Delete"
                            sx={{ color: '#ea4335' }}
                          >
                            <FaTrash size={16} />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Box>
                );
              })}

              {/* Empty State */}
              {folders.length === 0 && files.length === 0 && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 4,
                  color: '#666'
                }}>
                  <FaFolder size={48} color="#e0e0e0" />
                  <Typography variant="h6" sx={{ mt: 2, color: '#999' }}>
                    No files or folders
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    {canEdit ? "Upload files or create folders to get started" : "This folder is empty"}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </div>
      </Paper>

{/* Move File Dialog - WITH MANUAL HOME BUTTON */}
      <Dialog
        open={moveDialogOpen}
        onClose={() => {
          setMoveDialogOpen(false);
          setSelectedFileToMove('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Select Destination Folder
          <Typography variant="caption" display="block" sx={{ color: '#666', mt: 0.5 }}>
            Moving: <strong>{selectedFileToMove.split('/').pop()}</strong>
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {loadingFolders ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
              <CircularProgress />
              <Typography variant="caption" sx={{ mt: 2, color: '#666' }}>
                Loading folders...
              </Typography>
            </Box>
          ) : (
            <>
              {/* MANUAL HOME BUTTON - Always visible if file is in a subfolder */}
              {selectedFileToMove.includes('/') && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: '#e7f4f6', borderRadius: 2, border: '2px solid #00aaaf' }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1, color: '#00aaaf' }}>
                    🏠 Quick Move to Home
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: '#666' }}>
                    Move this file to the top-level Home folder (no folder path)
                  </Typography>
                  <Button
                    onClick={() => {
                      console.log('🏠 MANUAL HOME MOVE - User clicked manual Home button');
                      console.log('   File:', selectedFileToMove);
                      console.log('   Destination: Home (empty string)');
                      executeMove(''); // Empty string = Home
                    }}
                    fullWidth
                    variant="contained"
                    sx={{
                      backgroundColor: '#00aaaf',
                      '&:hover': {
                        backgroundColor: '#008c91'
                      },
                      fontWeight: 600,
                      py: 1.5
                    }}
                    startIcon={<span style={{ fontSize: '1.5rem' }}>🏠</span>}
                  >
                    Move to Home Folder
                  </Button>
                </Box>
              )}

              {/* Folder List */}
              {availableFolders.length === 0 ? (
                <Box sx={{ textAlign: 'center', p: 3, color: '#999' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                    ⚠️ No folders loaded
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 2 }}>
                    Use the "Move to Home Folder" button above, or try reloading
                  </Typography>
                  <Button
                    onClick={() => fetchAllFolders()}
                    style={{
                      backgroundColor: '#00aaaf',
                      color: 'white',
                      fontSize: 12,
                      padding: '8px 16px'
                    }}
                  >
                    🔄 Reload Folders
                  </Button>
                </Box>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#666' }}>
                    Or choose a specific folder:
                  </Typography>

                  <List sx={{ pt: 0 }}>
                    {availableFolders.map((folder, index) => {
                      const isHomeFolder = folder === '';
                      const displayName = isHomeFolder ? 'Home Folder' : folder;
                      const icon = isHomeFolder ? '🏠' : '📁';

                      return (
                        <ListItem key={index} disablePadding>
                          <ListItemButton
                            onClick={() => {
                              console.log(`📤 Moving to: "${folder}" (${isHomeFolder ? 'Home' : 'Subfolder'})`);
                              executeMove(folder);
                            }}
                            sx={{
                              borderRadius: 1,
                              mb: 0.5,
                              border: isHomeFolder ? '2px solid #00aaaf' : '1px solid #dee2e6',
                              backgroundColor: isHomeFolder ? '#f0f9fa' : 'white',
                              '&:hover': {
                                bgcolor: '#e7f4f6',
                                borderColor: '#00aaaf'
                              }
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <Box sx={{ fontSize: 24 }}>
                                {icon}
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={displayName}
                              secondary={isHomeFolder ? 'Home folder' : null}
                              primaryTypographyProps={{
                                sx: {
                                  color: '#00aaaf',
                                  fontWeight: isHomeFolder ? 700 : 500,
                                  fontSize: isHomeFolder ? '1.1rem' : '1rem'
                                }
                              }}
                            />
                            {isHomeFolder && (
                              <Box sx={{
                                backgroundColor: '#00aaaf',
                                color: 'white',
                                fontSize: '0.65rem',
                                padding: '3px 10px',
                                borderRadius: '12px',
                                fontWeight: 700
                              }}>
                                HOME
                              </Box>
                            )}
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              )}

              {/* Help text */}
              <Box sx={{
                mt: 2,
                p: 1.5,
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                borderLeft: '3px solid #00aaaf'
              }}>
                <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                  💡 <strong>Tip:</strong> The manual "Move to Home Folder" button above always works, even if the folder list has issues.
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
            Check console (F12) for detailed logs
          </Typography>
          <Button
            onClick={() => {
              setMoveDialogOpen(false);
              setSelectedFileToMove('');
            }}
            style={{
              backgroundColor: '#ea4335',
              color: 'white',
              fontSize: 12,
              padding: '6px 16px'
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <BottomTabNavigation />
    </Box>
  );
};

export default Documents;